import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  TrendingDown,
  ShieldCheck,
  Target,
  LineChart,
  FolderLock,
  ArrowLeft,
  Eye,
  PencilLine,
  Moon,
  Sun,
  Menu,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import NotificationBell from "../NotificationBell";

// The advisor's view of a single client. A deliberately separate shell from the
// personal dashboard: emerald identity, a persistent "viewing client" banner,
// and ONLY the modules the client actually shared — no Settings, Access
// Management, Family or billing surfaces leak into the advisor experience.
const MODULE_NAV = [
  { module: "dashboard", to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { module: "assets", to: "/dashboard/assets", label: "Assets", icon: Wallet },
  { module: "liabilities", to: "/dashboard/liabilities", label: "Liabilities", icon: TrendingDown },
  { module: "insurance", to: "/dashboard/insurance", label: "Insurance", icon: ShieldCheck },
  { module: "goals", to: "/dashboard/goals", label: "Goals", icon: Target },
  { module: "dashboard", to: "/dashboard/projections", label: "Projections", icon: LineChart },
  { module: "documents", to: "/dashboard/documents", label: "Documents", icon: FolderLock },
];

export default function AdvisorClientLayout({ children }) {
  const { activeClient, clearClient } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);

  // Advisor feedback when a write is transparently queued for client approval.
  useEffect(() => {
    const onQueued = () =>
      toast.success("Change sent to the client for approval", {
        description: "Nothing is applied until the client approves it.",
      });
    window.addEventListener("pfa:request-queued", onQueued);
    return () => window.removeEventListener("pfa:request-queued", onQueued);
  }, []);

  const shared = activeClient?.modules || {};
  const canSuggest = activeClient?.permission === "suggest";

  // Only surface modules the client shared (dedupe routes that map to the same
  // module, e.g. Overview + Projections both need "dashboard").
  const nav = MODULE_NAV.filter((it) => shared[it.module]);

  const backToClients = () => {
    clearClient();
    navigate("/advisor");
  };

  const Rail = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      {/* CLIENT IDENTITY */}
      <div className="px-5 h-16 flex items-center gap-3 border-b border-slate-200 dark:border-slate-800">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-500/20 dark:to-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-bold ring-1 ring-emerald-200 dark:ring-emerald-500/30">
          {activeClient?.name?.[0]?.toUpperCase() || "C"}
        </span>
        <div className="min-w-0">
          <div className="truncate font-display font-bold text-slate-900 dark:text-white">
            {activeClient?.name || "Client"}
          </div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            {canSuggest ? (
              <>
                <PencilLine className="h-3 w-3" /> View + Edit
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" /> View only
              </>
            )}
          </div>
        </div>
      </div>

      {/* NAV */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {nav.length === 0 && (
          <p className="px-3 py-2 text-xs text-slate-400">
            This client hasn't shared any modules yet.
          </p>
        )}
        {nav.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                isActive
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`
            }
          >
            <it.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{it.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* FOOTER */}
      <div className="px-3 py-4 border-t border-slate-200 dark:border-slate-800 space-y-1">
        <button
          onClick={toggle}
          className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          Appearance
        </button>
        <button
          onClick={backToClients}
          className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
        >
          <Users className="h-4 w-4" />
          Back to clients
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* DESKTOP RAIL */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
        <Rail />
      </aside>

      {/* MOBILE RAIL */}
      {mobileNav && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileNav(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
            <Rail onNavigate={() => setMobileNav(false)} />
          </aside>
        </>
      )}

      <div className="flex-1 lg:ml-64">
        {/* PERSISTENT CLIENT BANNER — soft emerald, not a heavy fill */}
        <div className="sticky top-0 z-30 bg-emerald-50/90 dark:bg-emerald-500/10 backdrop-blur border-b border-emerald-200 dark:border-emerald-500/20">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setMobileNav(true)}
                className="lg:hidden grid h-8 w-8 place-items-center rounded-lg text-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
              >
                <Menu className="h-4 w-4" />
              </button>
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
                <Eye className="h-3.5 w-3.5" />
              </span>
              <p className="text-xs sm:text-sm font-medium truncate text-emerald-900 dark:text-emerald-200">
                Viewing client:{" "}
                <span className="font-bold">{activeClient?.name}</span>
                <span className="ml-2 hidden sm:inline text-emerald-700/80 dark:text-emerald-300/80">
                  {canSuggest
                    ? "· You can propose changes (client approval required)"
                    : "· Read-only"}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <NotificationBell />
              <button
                onClick={backToClients}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 whitespace-nowrap"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Exit client view</span>
                <span className="sm:hidden">Exit</span>
              </button>
            </div>
          </div>
        </div>

        <main className="p-4 sm:p-6 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
